import React, { useState, useEffect, useCallback } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../hooks/msalConfig';
import GetFetchGraph from '../helper/GetFetchGraph';

// Bandeja de correo institucional. Solo funciona para quienes se autentican
// con Microsoft: la lectura del buzón requiere un token de Graph (Mail.Read).
const BandejaCorreo = () => {
    const [account, setAccount] = useState(null);
    const [mensajes, setMensajes] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);

    // Token silencioso; si caducó la sesión, pide interacción por popup.
    const obtenerToken = useCallback(async (acc) => {
        try {
            const resp = await msalInstance.acquireTokenSilent({ ...loginRequest, account: acc });
            return resp.accessToken;
        } catch (e) {
            if (e instanceof InteractionRequiredAuthError) {
                const resp = await msalInstance.acquireTokenPopup(loginRequest);
                return resp.accessToken;
            }
            throw e;
        }
    }, []);

    const cargarMensajes = useCallback(async (acc) => {
        setCargando(true);
        setError(null);
        try {
            const token = await obtenerToken(acc);
            const data = await GetFetchGraph.getMensajes(token, 25);
            setMensajes(data.value || []);
        } catch (e) {
            console.log(e);
            setError('No se pudieron cargar los correos. Intenta reconectar tu cuenta.');
        } finally {
            setCargando(false);
        }
    }, [obtenerToken]);

    // Si ya hay una sesión Microsoft activa, carga la bandeja al entrar.
    useEffect(() => {
        const cuentas = msalInstance.getAllAccounts();
        if (cuentas.length > 0) {
            setAccount(cuentas[0]);
            cargarMensajes(cuentas[0]);
        }
    }, [cargarMensajes]);

    const conectar = async () => {
        setError(null);
        try {
            const resp = await msalInstance.loginPopup(loginRequest);
            setAccount(resp.account);
            cargarMensajes(resp.account);
        } catch (e) {
            console.log(e);
            setError('No se pudo conectar con tu cuenta de Microsoft.');
        }
    };

    const desconectar = async () => {
        const acc = account;
        setAccount(null);
        setMensajes([]);
        await msalInstance.logoutPopup({ account: acc }).catch(() => {});
    };

    const formatoFecha = (iso) => {
        try {
            return new Date(iso).toLocaleString('es-SV', {
                day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
            });
        } catch (e) {
            return iso;
        }
    };

    // Sin sesión Microsoft: invitar a conectar.
    if (!account) {
        return (
            <div className="card mt-4">
                <div className="card-body text-center py-5">
                    <p className="display-4 text-primary mb-3">
                        <i className="fa fa-inbox" aria-hidden="true"></i>
                    </p>
                    <h3 className="h5 mb-2">Ver tus correos en la app</h3>
                    <p className="text-muted mb-4">
                        Conecta tu cuenta institucional de Microsoft para ver los correos que has recibido.
                    </p>
                    {error ? <div className="alert alert-danger">{error}</div> : null}
                    <button className="btn btn-primary" onClick={conectar}>
                        <i className="fa fa-sign-in mr-2" aria-hidden="true"></i>
                        Conectar correo institucional
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="h5 mb-0">Bandeja de entrada</h3>
                <div>
                    <button
                        className="btn btn-sm btn-outline-primary mr-2"
                        onClick={() => cargarMensajes(account)}
                        disabled={cargando}
                    >
                        <i className="fa fa-refresh mr-1" aria-hidden="true"></i>
                        Actualizar
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={desconectar}>
                        <i className="fa fa-sign-out mr-1" aria-hidden="true"></i>
                        Desconectar
                    </button>
                </div>
            </div>

            {error ? <div className="alert alert-danger">{error}</div> : null}

            {cargando ? (
                <div className="text-center py-5 text-muted">
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    Cargando correos…
                </div>
            ) : mensajes.length === 0 ? (
                <div className="card">
                    <div className="card-body text-center text-muted py-5">
                        No hay correos para mostrar.
                    </div>
                </div>
            ) : (
                <div className="list-group">
                    {mensajes.map((m) => (
                        <a
                            key={m.id || m.webLink}
                            href={m.webLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`list-group-item list-group-item-action ${m.isRead ? '' : 'font-weight-bold'}`}
                        >
                            <div className="d-flex justify-content-between">
                                <span className="text-truncate mr-2">
                                    {m.from && m.from.emailAddress ? m.from.emailAddress.name : 'Desconocido'}
                                </span>
                                <small className="text-muted text-nowrap">{formatoFecha(m.receivedDateTime)}</small>
                            </div>
                            <div className="text-truncate">{m.subject || '(sin asunto)'}</div>
                            <small className="text-muted d-block text-truncate">{m.bodyPreview}</small>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BandejaCorreo;
